import { contextFilesApi, digestsApi, matrixGenerationApi } from '@/lib/api';
import { DIGEST_COMMENT_MAX_CHARS } from '@mss_creator/shared';
import { Alert, Button, Icon } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { DigestCommentField } from './digest-comment-field';
import { digestFailureMessage, phaseLabel } from './digest-messages';
import { commentTooLongMessage } from './digest-version-card';
import { FileDigestCard } from './file-digest-card';
import { SynthesisDigestCard } from './synthesis-digest-card';

/**
 * Reviewing and steering the digests of a matrix's context files (2026-08-26
 * design doc, D23).
 *
 * One component, two homes: wizard step 2 and the matrix view's Contexte tab.
 * It therefore knows nothing about the wizard — it takes a matrix id and a
 * lock flag, and reads everything else from `GET /digest-state`.
 *
 * Why a review surface exists at all (D15/D16): the digests are what actually
 * reach the generation prompt, and a generation is expensive. Preparing them
 * here lets the user read and fix the model's understanding of their documents
 * *before* committing to a full generation — and because phases already up to
 * date are skipped, that preparation costs the later generation nothing.
 */

const JOB_POLL_INTERVAL_MS = 3_000;

/** Both bulk fields offer the same example: it shows the kind of steer a digest can take. */
const BULK_COMMENT_HELP =
  'Oriente chaque digest de ce lancement et la synthèse qui suit. Ex : « concentre-toi sur les tableaux tarifaires, ignore les annexes ».';
const REDIGEST_COMMENT_HELP =
  'Oriente chaque digest redéroulé. Ex : « concentre-toi sur les tableaux tarifaires, ignore les annexes ».';

export interface DigestReviewPanelProps {
  matrixId: string;
  /** True while a generation is in flight — every control here is guarded server-side too. */
  disabled?: boolean;
  /**
   * The matrix's sujet/contexte as they stand *right now* — the wizard's
   * in-progress draft, not the last autosaved value (issue #1).
   *
   * Passed in rather than read from the matrix query on purpose. Step 2
   * autosaves on a 500ms debounce, so for half a second after each keystroke
   * the server's copy is behind what the user is looking at. Reading the query
   * would make the buttons lag the fields they depend on; reading the draft and
   * flushing before every call (see `onBeforeModelCall`) keeps client and
   * server from ever contradicting each other.
   */
  sujet?: string | null;
  contexte?: string | null;
  /** Flushes the caller's pending autosave, so the server sees the draft before a model call lands. */
  onBeforeModelCall?: () => void;
}

export function DigestReviewPanel({
  matrixId,
  disabled = false,
  sujet,
  contexte,
  onBeforeModelCall,
}: DigestReviewPanelProps) {
  const queryClient = useQueryClient();

  /**
   * The file list is read here as well, and its ids go into the digest-state
   * query key. That is not a duplicate fetch — `ContextFilesField` owns the
   * same `['context-files', matrixId]` entry and TanStack Query dedupes it —
   * it is how an upload reaches this panel: uploading invalidates that key,
   * the id set changes, and the digest state is refetched under a new key
   * without this component watching anything imperatively.
   */
  const filesQuery = useQuery({
    queryKey: ['context-files', matrixId],
    queryFn: () => contextFilesApi.list(matrixId),
  });
  const fileSetKey = [...(filesQuery.data ?? [])]
    .map((file) => file.id)
    .sort()
    .join(',');

  /**
   * Digesting a whole matrix is several model calls, so it runs as a
   * `digest_only` job on the same table and the same polling machinery as a
   * generation (D15) — which is also what stops a manual digest and a
   * generation from telescoping.
   */
  const jobQuery = useQuery({
    queryKey: ['generation-job', matrixId],
    queryFn: () => matrixGenerationApi.getLatest(matrixId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'pending' || status === 'running' ? JOB_POLL_INTERVAL_MS : false;
    },
  });

  const job = jobQuery.data ?? null;
  const jobActive = job?.status === 'pending' || job?.status === 'running';
  const digestJobRunning = job?.mode === 'digest_only' && jobActive;
  const digestJobFailed = job?.mode === 'digest_only' && job.status === 'failed';

  const stateQuery = useQuery({
    queryKey: ['digest-state', matrixId, fileSetKey],
    queryFn: () => digestsApi.getState(matrixId),
  });

  // Not data fetching (CLAUDE.md's rule) but cache invalidation on a status
  // transition, mirroring the add-dimension job in `matrices.$matrixId.tsx`:
  // the digest rows the job wrote are only readable once it reports done, and
  // polling the state alongside the job would race its final write.
  const previousJobStatusRef = useRef<string | undefined>(undefined);
  // biome-ignore lint/correctness/useExhaustiveDependencies: only jobQuery.data should re-trigger this — matrixId/queryClient are stable.
  useEffect(() => {
    const previousStatus = previousJobStatusRef.current;
    previousJobStatusRef.current = job?.status;
    const justCompleted =
      (previousStatus === 'pending' || previousStatus === 'running') &&
      (job?.status === 'succeeded' || job?.status === 'failed');
    if (!justCompleted) return;
    void queryClient.invalidateQueries({ queryKey: ['digest-state', matrixId] });
    void queryClient.invalidateQueries({ queryKey: ['matrix', matrixId] });
  }, [job?.status]);

  /**
   * The two bulk comments are separate fields on purpose: the stale notice and
   * the « Préparer » button can be on screen together, and each comment orients
   * only the run its own button starts. Neither outlives that run (D18): a
   * field empties once its run has been accepted, and keeps its text if the
   * run fails to start.
   */
  const [bulkComment, setBulkComment] = useState('');
  const [redigestComment, setRedigestComment] = useState('');
  const [bulkCommentError, setBulkCommentError] = useState<string | null>(null);
  const [redigestCommentError, setRedigestCommentError] = useState<string | null>(null);

  const startJobMutation = useMutation({
    mutationFn: (comment: string | null) => {
      onBeforeModelCall?.();
      return digestsApi.startDigestJob(matrixId, comment === null ? {} : { comment });
    },
    // Seed the polled entry with the job the POST just returned, so the
    // pending state appears on the click rather than up to one poll later.
    onSuccess: (created) => {
      queryClient.setQueryData(['generation-job', matrixId], created);
      setBulkComment('');
    },
  });

  /** Trims, and refuses an over-long comment inline instead of spending a round trip on a 400. */
  function readComment(
    raw: string,
    setError: (message: string | null) => void,
  ): string | null | 'too-long' {
    const trimmed = raw.trim();
    if (trimmed.length > DIGEST_COMMENT_MAX_CHARS) {
      setError(commentTooLongMessage(trimmed.length));
      return 'too-long';
    }
    setError(null);
    return trimmed.length > 0 ? trimmed : null;
  }

  function startBulkJob() {
    const comment = readComment(bulkComment, setBulkCommentError);
    if (comment === 'too-long') return;
    startJobMutation.mutate(comment);
  }

  const state = stateQuery.data;
  const files = state?.files ?? [];
  const digestedCount = files.filter((file) => file.currentVersion !== null).length;
  // D11: below two digested files there is no synthesis to make, and both
  // synthesis routes answer 409 — so the card is not offered at all.
  const showSynthesis = digestedCount >= 2;
  const locked = disabled || digestJobRunning || startJobMutation.isPending;

  /**
   * Mirrors `assertFramed` server-side (issue #1) — same trim, same "either
   * field empty" rule, so the button the user sees and the answer the server
   * would give cannot disagree.
   *
   * A caller that passes neither field gets no gate: it is saying it does not
   * track the framing, and guessing on its behalf would either lock a control
   * that works or unlock one that 422s. There the failure copy carries it.
   */
  const framingMissing =
    sujet !== undefined && contexte !== undefined && (!sujet?.trim() || !contexte?.trim());

  const staleFileIds = state?.staleFileIds ?? [];
  const staleFiles = files.filter((file) => staleFileIds.includes(file.contextFileId));
  const staleManualCount = staleFiles.filter(
    (file) => file.currentVersion?.origin === 'manual',
  ).length;

  /**
   * Re-runs the stale digests, one at a time and only when asked.
   *
   * Sequential rather than parallel, for the same reason the digest job itself
   * is: each is a minutes-long model call on one shared credential. Failures do
   * not abort the rest — a document that fails should not strand the ones after
   * it — and the count of failures is what the message reports.
   *
   * The comment goes to every digest re-run. The synthesis is not re-run here,
   * so there is nothing to pass it (issue #20, H13).
   */
  const redigestStaleMutation = useMutation({
    mutationFn: async (comment: string | null) => {
      onBeforeModelCall?.();
      let failed = 0;
      for (const file of staleFiles) {
        try {
          await digestsApi.regenerateFileDigest(
            matrixId,
            file.contextFileId,
            comment === null ? {} : { comment },
          );
        } catch {
          failed += 1;
        }
      }
      return failed;
    },
    // Every failed document keeps the comment in its field for a retry on the card.
    onSuccess: (failed) => {
      if (failed === 0) setRedigestComment('');
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['digest-state', matrixId] });
      void queryClient.invalidateQueries({ queryKey: ['matrix', matrixId] });
    },
  });

  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="font-semibold text-small">Digests des documents</span>
        <span className="text-caption text-gray-600">Facultatif</span>
      </div>
      <p className="mt-1 text-gray-600 text-small">
        Chaque document est résumé, puis les résumés sont réconciliés entre eux. C’est ce texte —
        pas les documents bruts — qui est fourni au modèle. Vous pouvez le relire, l’orienter par un
        commentaire, ou le réécrire à la main.
      </p>

      {stateQuery.isLoading ? (
        <p className="mt-3 text-gray-500 text-small">Chargement des digests…</p>
      ) : files.length === 0 ? (
        // Nothing is wrong here — documents are optional (D23), so this is the
        // default state of the step and must not read as a failure.
        <p className="mt-3 text-gray-500 text-small">
          Aucun document de contexte pour l’instant : les digests apparaîtront ici dès qu’un
          document sera téléversé.
        </p>
      ) : (
        <div className="mt-3 space-y-3">
          {framingMissing && (
            <div
              data-testid="digest-framing-notice"
              className="rounded-md border-2 border-bleu-ciel bg-bleu-ciel-tint px-3 py-2 text-small"
            >
              Renseignez le sujet et le contexte pour préparer les digests. Le digest extrait ce qui
              est pertinent <em>pour cette matrice</em> : sans cadrage, il n’a aucun moyen de
              distinguer un chiffre décisif d’un chiffre accessoire. Vous pouvez toujours saisir un
              digest à la main.
            </div>
          )}

          {staleFiles.length > 0 && (
            <div
              data-testid="digest-stale-notice"
              className="rounded-md border-2 border-warning bg-warning-tint px-3 py-2 text-small"
            >
              <p>
                {staleFiles.length === 1
                  ? 'Un digest a été produit sous le cadrage précédent : il a filtré le document selon un sujet qui n’est plus celui de la matrice.'
                  : `${staleFiles.length} digests ont été produits sous le cadrage précédent : ils ont filtré leur document selon un sujet qui n’est plus celui de la matrice.`}
              </p>
              <p className="mt-1 text-gray-700">
                Rien n’est redéroulé automatiquement — un redéroulage coûte un appel modèle par
                document
                {staleManualCount > 0
                  ? `, et ${staleManualCount === 1 ? 'un de ces digests a été écrit' : `${staleManualCount} de ces digests ont été écrits`} à la main : le texte saisi sera remplacé`
                  : ''}
                .
              </p>
              <div className="mt-2 max-w-2xl">
                <DigestCommentField
                  help={REDIGEST_COMMENT_HELP}
                  value={redigestComment}
                  onChange={setRedigestComment}
                  disabled={locked || framingMissing || redigestStaleMutation.isPending}
                  invalid={redigestCommentError !== null}
                />
              </div>
              {redigestCommentError && <Alert tone="danger">{redigestCommentError}</Alert>}
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => {
                  const comment = readComment(redigestComment, setRedigestCommentError);
                  if (comment === 'too-long') return;
                  redigestStaleMutation.mutate(comment);
                }}
                disabled={locked || framingMissing || redigestStaleMutation.isPending}
              >
                <Icon name="auto_awesome" size={16} />
                {staleFiles.length === 1
                  ? 'Redérouler le digest concerné'
                  : `Redérouler les ${staleFiles.length} digests concernés`}
              </Button>
              {redigestStaleMutation.isPending && (
                <output className="mt-2 block text-gray-700 text-small">
                  Redéroulage en cours — un appel au modèle par document.
                </output>
              )}
              {redigestStaleMutation.isSuccess && redigestStaleMutation.data > 0 && (
                <Alert tone="danger">
                  {redigestStaleMutation.data === 1
                    ? 'Un digest n’a pas pu être redéroulé. Réessayez sur la carte concernée.'
                    : `${redigestStaleMutation.data} digests n’ont pas pu être redéroulés. Réessayez sur les cartes concernées.`}
                </Alert>
              )}
            </div>
          )}

          {digestJobRunning && (
            <output className="block text-gray-600 text-small">
              {phaseLabel(job?.phase ?? null)} Cette étape peut prendre plusieurs minutes.
            </output>
          )}

          {digestJobFailed && (
            <Alert tone="danger">
              La préparation des digests a échoué. Relancez-la, ou saisissez les digests à la main.
            </Alert>
          )}

          {state?.ready && staleFiles.length === 0 ? (
            <p className="text-small text-success">
              Digests à jour — la génération n’aura aucun document à analyser.
            </p>
          ) : (
            !digestJobRunning && (
              <div>
                <div className="max-w-2xl">
                  <DigestCommentField
                    help={BULK_COMMENT_HELP}
                    value={bulkComment}
                    onChange={setBulkComment}
                    disabled={locked || framingMissing}
                    invalid={bulkCommentError !== null}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={startBulkJob}
                  disabled={locked || framingMissing}
                >
                  <Icon name="auto_awesome" size={16} />
                  Préparer les digests maintenant
                </Button>
                <p className="mt-1 text-caption text-gray-600">
                  Sinon, ils seront produits automatiquement au lancement de la génération.
                </p>
                {bulkCommentError && <Alert tone="danger">{bulkCommentError}</Alert>}
                {startJobMutation.isError && (
                  <Alert tone="danger">{digestFailureMessage(startJobMutation.error)}</Alert>
                )}
              </div>
            )
          )}

          {files.map((file) => (
            <FileDigestCard
              key={file.contextFileId}
              matrixId={matrixId}
              file={file}
              disabled={locked || redigestStaleMutation.isPending}
              framingMissing={framingMissing}
              stale={staleFileIds.includes(file.contextFileId)}
              {...(onBeforeModelCall ? { onBeforeModelCall } : {})}
            />
          ))}

          {showSynthesis ? (
            <SynthesisDigestCard
              matrixId={matrixId}
              synthesis={state?.synthesis ?? null}
              current={state?.synthesisCurrent ?? false}
              disabled={locked || redigestStaleMutation.isPending}
              framingMissing={framingMissing}
              stale={state?.synthesisStale ?? false}
              {...(onBeforeModelCall ? { onBeforeModelCall } : {})}
            />
          ) : (
            // D11 again, said out loud: with one document the absence of a
            // synthesis is correct, not a missing piece.
            digestedCount === 1 && (
              <p className="text-gray-600 text-small">
                Un seul document : son digest sert directement de contexte, il n’y a pas de synthèse
                à produire.
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
}
